//frontend/src/pages/HomePage.js

import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import axios from "axios";
import "../styles/HomePage.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;


const HomePage = () => {
  const [subscriptions, setSubscriptions] = useState(() => {
    const cachedSubscriptions = localStorage.getItem("subscriptions");
    return cachedSubscriptions ? JSON.parse(cachedSubscriptions) : [];
  });
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [errorCustomer, setErrorCustomer] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notification, setNotification] = useState(null);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(subscriptions.length === 0);

  const [orderHistory, setOrderHistory] = useState([]);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [seatsToAdd, setSeatsToAdd] = useState(0);
  const [maxSeats, setMaxSeats] = useState(null);

const fetchSubscriptions = async (forceRefresh = false) => {
  setLoading(true);
  setError(null);

  try {
    const userString = localStorage.getItem("user");
    if (!userString) throw new Error("No valid authentication token found.");
    const token = JSON.parse(userString).token;

    let attempts = 0;
    const maxAttempts = 5;
    let fetchedData = null;

    // 🚀 Step 1: Request subscriptions (cached or fresh)
    const response = await axios.get(
      `${API_URL}/subscriptions?force_refresh=${forceRefresh}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (response.status === 200) {
      setSubscriptions(response.data.subscriptions || []);

      if (!response.data.loading) {
        // ✅ If fresh data is available, stop here
        localStorage.setItem(
          "subscriptions",
          JSON.stringify(response.data.subscriptions)
        );
        setLoading(false);
        return;
      }
    } else {
      throw new Error("Unexpected response from server.");
    }

    // 🚀 Step 2: Poll until fresh data is available
    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 2000)); // ⏳ Wait before retrying

      const retryResponse = await axios.get(
        `${API_URL}/subscriptions?force_refresh=false`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (retryResponse.status === 200 && !retryResponse.data.loading) {
        fetchedData = retryResponse.data.subscriptions;
        break; // ✅ Stop polling when fresh data is available
      }

      attempts++;
    }

    if (!fetchedData) throw new Error("Failed to load fresh subscriptions.");

    setSubscriptions(fetchedData);
    localStorage.setItem("subscriptions", JSON.stringify(fetchedData));
  } catch (err) {
    setSubscriptions([]); // Ensure UI doesn't blink old data
    setError(err.message || "Failed to load subscriptions.");
  } finally {
    setLoading(false);
  }
};

  // Fetch customer info
    const fetchCustomerInfo = async () => {
      setLoadingCustomer(true);
      setErrorCustomer(null);

      try {
        const userString = localStorage.getItem("user");
        if (!userString)
          throw new Error("No valid authentication token found.");
        const token = JSON.parse(userString).token;

        const response = await axios.get(`${API_URL}/customer-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.status === 200) {
          setCustomerInfo(response.data); // ✅ Correctly updating customerInfo state
        } else {
          throw new Error("Failed to fetch customer information.");
        }
      } catch (error) {
        console.error("Error fetching customer info:", error);
        setErrorCustomer(error.message || "Failed to load customer info.");
        setCustomerInfo(null);
      } finally {
        setLoadingCustomer(false);
      }
    };
  // Fetch customer info on mount
  useEffect(() => {
    fetchCustomerInfo();
  }, []);

  // Fetch subscriptions on mount
  useEffect(() => {
    if (subscriptions.length === 0) {
      fetchSubscriptions();
    }
  }, [subscriptions.length]); // Run only once on mount

  const fetchSeatLimit = async () => {
    try {
      const userString = localStorage.getItem("user");
      if (!userString) throw new Error("No valid authentication token found.");

      const parsedUser = JSON.parse(userString);
      const token = parsedUser.token;

      const response = await axios.get(`${API_URL}/user/seat_limit`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setMaxSeats(response.data.seat_add_limit || 10); // Default to 10 if null
      } else {
        throw new Error("Failed to fetch seat limit.");
      }
    } catch (error) {
      console.error("Error fetching seat limit:", error);
      setMaxSeats(10); // Fallback value
    }
  };

  // Fetch seat limit on mount
  useEffect(() => {
    fetchSeatLimit();
  }, []);


  // Open/Close modal
  const openModal = (sub) => {
    setSelectedSubscription(sub);
    setSeatsToAdd(0);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedSubscription(null);
    setSeatsToAdd(0);
    setShowModal(false);
  };

  // Handle adding seats
  const [confirmWarning, setConfirmWarning] = useState(false);

const handleConfirmWarning = () => {
  setConfirmWarning(true);
};

const handleAddSeats = async () => {
  if (!confirmWarning) {
    alert("You must confirm before adding seats.");
    return;
  }

  try {
    const userString = localStorage.getItem("user");
    if (!userString) {
      throw new Error("No valid authentication token found.");
    }
    const token = JSON.parse(userString).token;

    if (seatsToAdd <= 0) {
      alert("Please enter a positive number of seats to add.");
      return;
    }

    setIsProcessing(true);
    setProgress(20);

    const response = await axios.post(
      `${API_URL}/subscriptions/add_seats`,
      {
        subscription_id: selectedSubscription.id,
        seats_to_add: seatsToAdd,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setProgress(60);

    if (response.status === 200) {
      setProgress(100);
      setNotification({
        type: "success",
        message: `${seatsToAdd} seats were successfully added!`,
      });

      // ✅ Log this event
      await axios.post(
        `${API_URL}/log_seat_addition`,
        {
          subscription_id: selectedSubscription.id,
          seats_added: seatsToAdd,
          user_email: JSON.parse(userString).email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      await fetchSubscriptions();
      closeModal();
    } else {
      throw new Error(response.data.detail || "Failed to add seats.");
    }
  } catch (error) {
    setNotification({
      type: "error",
      message:
        error.response?.data?.detail ||
        error.message ||
        "An unexpected error occurred.",
    });
  } finally {
    setIsProcessing(false);
    setProgress(0);
  }
};

const fetchOrderHistory = async () => {
  try {
    const userString = localStorage.getItem("user");
    if (!userString) throw new Error("No valid authentication token found.");
    const token = JSON.parse(userString).token;

    const response = await axios.get(`${API_URL}/order_history`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.status === 200) {
      setOrderHistory(response.data);
    }
  } catch (error) {
    console.error("Error fetching order history:", error);
  }
};

useEffect(() => {
  fetchOrderHistory();
}, []);


  return (
    <div className="bg-white text-gray-800">
      <Header />
      <main className="mx-auto p-6 space-y-6">
        {/* Contact Info + Quick Links */}
        <div className="container info-wrap grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="contact-info p-6">
            <h2 className="mb-4">Contact Information</h2>
            {loadingCustomer ? (
              <div className="flex justify-center items-center h-32">
                <div className="loader"></div>
              </div>
            ) : errorCustomer ? (
              <p className="text-red-500">{errorCustomer}</p>
            ) : customerInfo ? (
              <ul className="info-list">
                <li>
                  <div className="info-item">Domain:</div>{" "}
                  {customerInfo.customerDomain || "N/A"}
                </li>
                <li>
                  <div className="info-item">Cloud Identity ID:</div>{" "}
                  {customerInfo.id || "N/A"}
                </li>
                <li>
                  <div className="info-item">Client Name:</div>{" "}
                  {customerInfo.postalAddress?.contactName || "N/A"}
                </li>
                <li>
                  <div className="info-item">Email:</div>{" "}
                  {customerInfo.alternateEmail || "N/A"}
                </li>
                <li>
                  <div className="info-item">Phone:</div>{" "}
                  {customerInfo.postalAddress?.phoneNumber || "N/A"}
                </li>
                <li>
                  <div className="info-item">Address:</div>{" "}
                  {customerInfo.postalAddress
                    ? `${customerInfo.postalAddress.addressLine1 || ""}, ${
                        customerInfo.postalAddress.locality || ""
                      }, ${customerInfo.postalAddress.region || ""}, ${
                        customerInfo.postalAddress.postalCode || ""
                      } ${customerInfo.postalAddress.countryCode || ""}`
                    : "N/A"}
                </li>
              </ul>
            ) : (
              <p className="text-gray-500">
                No customer information available.
              </p>
            )}
          </section>

          <section className="p-6 quick-links">
            <h2 className="mb-4">Quick Links</h2>
            <ul className="space-y-2">
              <li>
                <a
                  href="https://admin.google.com"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Admin Console
                </a>
              </li>
              <li>
                <a
                  href="https://admin.google.com/ac/users"
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Manage Users
                </a>
              </li>
              <li>
                <a
                  href="mailto:s@cloudfresh.com?subject=Support%20Request&body=Hello%2C%0A%0AI%20have%20a%20question%20about..."
                  class="text-primary hover:underline"
                >
                  Ask Cloudfresh Support
                </a>
              </li>
              <li>
                <a
                  href="#orderHistory"
                  className="text-primary hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("orderHistory").scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                >
                  History
                </a>
              </li>
            </ul>
          </section>
        </div>

        {/* Subscriptions */}
        <section className="subscriptions-container p-6">
          <h2 className="mb-4">Subscriptions</h2>
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="loader"></div>
            </div>
          ) : error ? (
            <p className="text-red-500">{error}</p>
          ) : subscriptions.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {subscriptions.map((subscription) => {
                const planUpper = (subscription.plan || "").toUpperCase();
                const billingUpper = (
                  subscription.billing_method || ""
                ).toUpperCase();
                const canAdd =
                  planUpper === "FLEXIBLE" || planUpper === "ANNUAL";

                return (
                  <div key={subscription.id} className="subscriptions-item p-4">
                    <h3>{subscription.sku_name}</h3>
                    <div className="subscriptions-list">
                      <p>
                        <div className="subscriptions-list-info">Status: </div>
                        <span
                          className={
                            subscription.status === "ACTIVE"
                              ? "text-green-600"
                              : "text-red-600"
                          }
                        >
                          {subscription.status}
                        </span>
                      </p>
                      <p>
                        <div className="subscriptions-list-info">Licenses:</div>
                        {subscription.totalSeats || subscription.seats || 0}
                      </p>
                      <p>
                        <div className="subscriptions-list-info">
                          Unassigned:
                        </div>
                        {subscription.unassignedSeats || 0}
                      </p>
                      <p>
                        <div className="subscriptions-list-info">Plan:</div>
                        {subscription.plan}
                      </p>
                    </div>

                    {canAdd && (
                      <button
                        onClick={() => openModal(subscription)}
                        className="subscriptions-item-btn rounded"
                      >
                        Add Seats
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No subscriptions available.</p>
          )}
          <button
            onClick={() => fetchSubscriptions(true)} // ✅ Force refresh
            className="subscriptions-btn rounded"
          >
            {loading ? "Refreshing..." : "Refresh Subscriptions"}
          </button>
        </section>
        <section id="orderHistory" className="history-container p-6">
          <div className="history-wrap container">
            <h2>Order History</h2>
            <div className="history-row history-row-headling">
              <div className="history-item">Timestamp</div>
              <div className="history-item">User</div>
              <div className="history-item">Action</div>
              <div className="history-item">Product</div>
              <div className="history-item">Quantity</div>
              <div className="history-item">Order ID</div>
            </div>
            {orderHistory.length > 0 ? (
              orderHistory.map((entry) => (
                <div className="history-row" key={entry.order_id}>
                  <div className="history-item">{entry.timestamp}</div>
                  <div className="history-item">{entry.user}</div>
                  <div className="history-item">{entry.action}</div>
                  <div className="history-item">{entry.product}</div>
                  <div className="history-item">{entry.quantity}</div>
                  <div className="history-item">{entry.order_id}</div>
                </div>
              ))
            ) : (
              <p className="text-gray-500">No order history found.</p>
            )}
          </div>
        </section>
      </main>

      {/* Modal for adding seats */}
      {showModal && selectedSubscription && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={closeModal}
        >
          <div
            className="modal-wrapper bg-white p-6 rounded shadow-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-semibold mb-4">
              Add seats to {selectedSubscription.sku_name}
            </h2>
            <div className="mb-4">
              <label htmlFor="seatsInput" className="block text-gray-700">
                Number of seats to add:
              </label>
              <input
                id="seatsInput"
                type="number"
                className="border p-2 w-full mt-1"
                placeholder={
                  maxSeats ? `You can add max ${maxSeats} seats` : "Enter seats"
                }
                min="1"
                value={seatsToAdd === 0 ? "" : seatsToAdd}
                onChange={(e) => {
                  const value = e.target.value;
                  setSeatsToAdd(
                    value === "" ? "" : Math.max(1, parseInt(value, 10) || 1)
                  );
                }}
              />
            </div>

            {/* 🚀 Show Progress Bar While Processing */}
            {isProcessing && (
              <div className="w-full bg-gray-200 rounded-md overflow-hidden mt-3">
                <div
                  className="bg-green-500 text-xs text-white text-center p-1"
                  style={{ width: `${progress}%` }}
                >
                  {progress}% Completed
                </div>
              </div>
            )}

            {/* 🚀 Show Notification After Success or Error */}
            {notification && (
              <div
                className={`mt-3 p-2 rounded-md ${
                  notification.type === "success"
                    ? "bg-green-200 text-green-800"
                    : "bg-red-200 text-red-800"
                }`}
              >
                {notification.message}
              </div>
            )}

            <div className="flex space-x-2 mt-4">
              <div className="mb-4">
                <p className="text-red-600 font-bold">
                  Attention: Adding seats is irreversible. You take full
                  responsibility.
                </p>
                <label className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={confirmWarning}
                    onChange={(e) => setConfirmWarning(e.target.checked)}
                    className="mr-2"
                  />
                  I understand and accept this condition.
                </label>
              </div>
              <button
                onClick={handleAddSeats}
                disabled={!confirmWarning || isProcessing} // 🔒 Disable while processing
                className={`green ${
                  !confirmWarning || isProcessing
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                } confirmation-item-btn`}
              >
                {isProcessing ? "Processing..." : "Confirm"}{" "}
                {/* Change button text while loading */}
              </button>
              <button
                onClick={closeModal}
                className="normal confirmation-item-btn"
                disabled={isProcessing} // 🔒 Prevent closing while processing
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
